// Fiche d'une étude QUESTIONNAIRE (062) : à qui l'envoyer (Participants), et ce
// qu'on a répondu (Résultats).
import { useState } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, Chip, ErrorBanner, Note, Spinner } from '../components/ui';
import { PeoplePicker } from '../people/PeoplePicker';
import { hrefFor } from '../router';
import {
  useCloseWave, useQuestionnaireResults, useRecipients, useRemoveRecipient, useRenewWaveLink, useSendQuestionnaire,
  type QuestionnaireResults as Results, type QuestionResult,
} from './questionnaire';
import './studies.css';

const errorOf = (e: unknown) => (e instanceof ApiRequestError ? e.message : 'Une erreur inattendue est survenue.');
const day = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/** LE LIEN COMMUN d'une vague : seule l'empreinte est gardée, on ne peut que le remplacer. */
function WaveLink({ studyId }: { studyId: string }) {
  const renew = useRenewWaveLink(studyId);
  const link = renew.data ? `${window.location.origin}${hrefFor(`/q/${renew.data.token}`)}` : null;
  return (
    <section className="test-block">
      <h2 className="t-h3">Lien ouvert</h2>
      <p className="zy-texte-muet">
        À partager où vous voulez, sans annuaire. Créer un lien remplace le précédent, qui cesse de fonctionner.
      </p>
      {link ? <Note tone="succes" role="status">{link}</Note> : null}
      {renew.error ? <ErrorBanner>{errorOf(renew.error)}</ErrorBanner> : null}
      <Button variant="ghost" loading={renew.isPending} onClick={() => renew.mutate()}>
        Créer un lien ouvert
      </Button>
    </section>
  );
}

/** PARTICIPANTS : choisir dans l'annuaire, envoyer, suivre qui a répondu. */
export function QuestionnaireParticipants({ projectId, studyId, canWrite, wave = false }: {
  projectId: string;
  studyId: string;
  canWrite: boolean;
  /** Vague de baromètre : un lien ouvert s'ajoute aux liens personnels. */
  wave?: boolean;
}) {
  const list = useRecipients(studyId);
  const send = useSendQuestionnaire(studyId);
  const remove = useRemoveRecipient(studyId);
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [links, setLinks] = useState<{ name: string; link: string; mailSent: boolean }[]>([]);
  const recipients = list.data?.recipients ?? [];
  const answered = recipients.filter((r) => r.status === 'repondu').length;
  const noQuestions = list.data && list.data.questions === 0;

  return (
    <>
      {wave && canWrite ? <WaveLink studyId={studyId} /> : null}
      {canWrite ? (
        <section className="test-block">
          <h2 className="t-h3">Envoyer le questionnaire</h2>
          {noQuestions ? (
            <Note tone="lecture">Le questionnaire n’a pas encore de question : préparez-le dans l’onglet Protocole.</Note>
          ) : (
            <>
              <p className="zy-texte-muet">Cochez les personnes de l’annuaire : chacune reçoit un lien personnel par e-mail, et répond sans compte.</p>
              <PeoplePicker projectId={projectId} selected={personIds} onChange={setPersonIds} max={200} />
              <label className="zy-field">
                <span className="zy-field__label">Un mot (facultatif)</span>
                <textarea className="zy-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder="Je suis Frédéric Rossi, UX Researcher. Pourriez-vous prendre cinq minutes pour répondre à ce questionnaire ?" />
              </label>
              {send.error ? <ErrorBanner>{errorOf(send.error)}</ErrorBanner> : null}
              <div className="st-actions">
                <span />
                <Button
                  variant="accent"
                  loading={send.isPending}
                  disabled={personIds.length === 0}
                  onClick={() => send.mutate({ personIds, message: message.trim() || undefined }, {
                    onSuccess: (r) => {
                      setPersonIds([]);
                      setLinks(r.sent.map((s) => ({ name: s.name, link: `${window.location.origin}${hrefFor(`/q/${s.token}`)}`, mailSent: s.mailSent })));
                    },
                  })}
                >
                  {personIds.length > 0 ? `Envoyer à ${personIds.length} personne${personIds.length > 1 ? 's' : ''}` : 'Envoyer'}
                </Button>
              </div>
              {links.length > 0 ? (
                <Note tone="succes" role="status">
                  {links.filter((l) => l.mailSent).length} e-mail{links.filter((l) => l.mailSent).length > 1 ? 's' : ''} envoyé{links.filter((l) => l.mailSent).length > 1 ? 's' : ''}.
                  {links.some((l) => !l.mailSent) ? ' Sans e-mail, transmettez vous-même le lien : ' : ''}
                  {links.filter((l) => !l.mailSent).map((l) => (
                    <span key={l.link} className="inv-sub">{l.name} : {l.link}</span>
                  ))}
                </Note>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      <section className="test-block">
        <div className="block-head">
          <h2 className="t-h3">Destinataires</h2>
          <span className="zy-texte-muet">{answered} réponse{answered > 1 ? 's' : ''} sur {recipients.length}</span>
        </div>
        {list.isPending ? <Spinner label="Chargement" /> : recipients.length === 0 ? (
          <p className="zy-texte-muet">Personne n’a encore reçu ce questionnaire.</p>
        ) : (
          <ul className="qr-list">
            {recipients.map((r) => (
              <li key={r.id}>
                <span><strong>{r.name}</strong>{r.email ? <span className="inv-sub">{r.email}</span> : null}</span>
                <Chip state={r.status === 'repondu' ? 'succes' : undefined}>
                  {r.status === 'repondu' ? `Répondu le ${day(r.answeredAt!)}` : `Envoyé le ${day(r.sentAt)}`}
                </Chip>
                {canWrite ? (
                  <Button variant="link-danger" size="s" type="button" disabled={remove.isPending} onClick={() => remove.mutate(r.id)}>
                    Retirer
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function ResultCard({ q }: { q: QuestionResult }) {
  const max = Math.max(1, ...(q.counts ?? []).map((c) => c.count));
  return (
    <li className="qres">
      <p className="qres-text">{q.text}</p>
      <p className="zy-texte-muet">
        {q.answered} réponse{q.answered > 1 ? 's' : ''}
        {q.type === 'echelle' && q.average !== null && q.average !== undefined ? <> · moyenne <strong>{q.average}</strong> / {q.max}</> : null}
      </p>
      {q.texts ? (
        q.texts.length === 0 ? null : (
          <ul className="qres-texts">{q.texts.map((t, i) => <li key={i}>« {t} »</li>)}</ul>
        )
      ) : q.type === 'echelle' ? (
        // Une échelle se lit en colonnes, de la note la plus basse à la plus haute.
        <ul className="qres-scale" aria-label="Répartition des notes">
          {(q.counts ?? []).map((c) => (
            <li key={c.label} title={`${c.label} : ${c.count}`}>
              <span className="qres-n">{c.count || ''}</span>
              <span className="qres-col"><span className="qres-bar" style={{ height: `${(c.count / max) * 100}%` }} /></span>
              <span className="qres-label">{c.label}</span>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="qres-bars">
          {(q.counts ?? []).map((c) => (
            <li key={c.label}>
              <span className="qres-label">{q.type === 'oui_non' ? (c.label === 'oui' ? 'Oui' : 'Non') : c.label}</span>
              <span className="qres-track"><span className="qres-bar" style={{ width: `${(c.count / max) * 100}%` }} /></span>
              <span className="qres-n">{c.count}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

const SUS_AVERAGE = 68;
const fr = (n: number) => n.toLocaleString('fr-FR');

/** Les scores d'une vague : NPS et SUS d'abord, puis les questions de pilier. */
function WaveHeadline({ studyId, data, canWrite }: { studyId: string; data: Results; canWrite: boolean }) {
  const close = useCloseWave(studyId);
  const scores = data.scores;
  return (
    <>
      {data.fragile ? (
        <Note tone="avertissement">
          Moins de 30 réponses : ces scores donnent une tendance, pas une mesure solide.
        </Note>
      ) : null}
      {scores ? (
        <div className="qres-scores">
          {scores.nps ? (
            <div className="zy-stat">
              <span className="zy-texte-muet">NPS, de −100 à +100</span>
              <span className="zy-stat__valeur">{scores.nps.value}</span>
              <span className="zy-texte-muet">
                {scores.nps.promoters} % promoteurs, {scores.nps.detractors} % détracteurs
              </span>
            </div>
          ) : null}
          {scores.sus ? (
            <div className="zy-stat">
              <span className="zy-texte-muet">SUS, de 0 à 100</span>
              <span className="zy-stat__valeur">{fr(scores.sus.value)}</span>
              <span className="zy-texte-muet">
                {scores.sus.value >= SUS_AVERAGE ? 'Au-dessus' : 'En dessous'} de la moyenne de {SUS_AVERAGE}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      {scores && scores.pillars.length > 0 ? (
        <ul className="qr-list">
          {scores.pillars.map((p) => (
            <li key={p.name}>
              <span>{p.name}</span>
              <strong>{fr(p.value)} / 5</strong>
            </li>
          ))}
        </ul>
      ) : null}
      {canWrite && !data.closed ? (
        <div className="st-actions">
          <span className="zy-texte-muet">Clore la vague ajoute ses scores aux courbes du projet.</span>
          <Button variant="accent" loading={close.isPending} disabled={Boolean(data.masked)} onClick={() => close.mutate()}>
            Clore la vague
          </Button>
        </div>
      ) : null}
      {close.error ? <ErrorBanner>{errorOf(close.error)}</ErrorBanner> : null}
    </>
  );
}

/** RÉSULTATS : l'agrégat, question par question. */
export function QuestionnaireResults({ studyId, canWrite = false }: { studyId: string; canWrite?: boolean }) {
  const r = useQuestionnaireResults(studyId);
  if (r.isPending) return <Spinner label="Calcul des résultats" />;
  if (r.isError) return <ErrorBanner>{errorOf(r.error)}</ErrorBanner>;
  const d = r.data;
  return (
    <section className="test-block">
      <div className="block-head">
        <h2 className="t-h3">Résultats</h2>
        <span className="zy-texte-muet">{d.respondents} répondant{d.respondents > 1 ? 's' : ''}{d.scores || d.masked ? '' : ` sur ${d.sent} envoi${d.sent > 1 ? 's' : ''}`}</span>
      </div>
      {d.masked === undefined ? null : <WaveHeadline studyId={studyId} data={d} canWrite={canWrite} />}
      {d.respondents === 0 ? (
        <p className="zy-texte-muet">Pas encore de réponse. Envoyez le questionnaire depuis l’onglet Participants.</p>
      ) : d.masked ? (
        <Note tone="lecture">
          Les résultats s’affichent à partir de {d.min} réponses, pour que personne ne soit reconnaissable.
        </Note>
      ) : (
        <ol className="qres-list">{d.questions.map((q) => <ResultCard key={q.key} q={q} />)}</ol>
      )}
    </section>
  );
}
